/**
 * Live ingest: a media worker follows a file that is still being written,
 * turning what has arrived into a live HLS proxy (fMP4 segments) that people
 * watch 6 to 10 seconds behind, until the file stops growing.
 */
import { z } from 'zod';
import { CONTRACT_VERSION, LocationSpecsSchema, ObjectRefSchema } from './jobs.ts';

export const LiveFollowJobSchema = z.object({
  contractVersion: z.literal(CONTRACT_VERSION),
  correlationId: z.string(),
  recordingId: z.string().uuid(),
  /** The growing file, in its watch folder. */
  source: ObjectRefSchema,
  /** Where the live proxy goes: the derivatives tier, under this prefix (ends with /). */
  destination: z.object({ locationId: z.string().uuid(), prefix: z.string().min(1).max(500) }),
  locations: LocationSpecsSchema,
  /** The recording ends when the file has not grown for this long. */
  closeAfterSeconds: z.number().int().min(5).max(3600),
});
export type LiveFollowJob = z.infer<typeof LiveFollowJobSchema>;

export interface LiveFollowResult {
  /** Seconds of media turned into the live proxy. */
  seconds: number;
  segments: number;
}

/** Media a live watch folder can follow while it grows (formats whose index is not at the end). */
export const LIVE_EXTENSIONS = new Set(['mxf', 'ts', 'm2ts', 'mts', 'mpg', 'mpeg', 'mkv', 'webm', 'mp4', 'm4v', 'mov']);

export interface LiveDTO {
  /** recording: growing now; finishing: the file closed and is being ingested; finished: done. */
  state: 'recording' | 'finishing' | 'finished' | 'failed';
  /** The live playlist (HLS), playable with the browser's own player or through MediaSource. */
  playlistUrl: string;
  startedAt: string;
  /** 1.9.1: the workspace's time zone at the start (live sources; else the workspace's), for "Recording since". */
  timeZone?: string | null;
  finishedAt: string | null;
  seconds: number | null;
  error: string | null;
  /**
   * Editing while recording (1.0.0-rc.1): the length the live proxy lists so far (ticks), and a signed snapshot
   * playlist of it closed with #EXT-X-ENDLIST, which plays as an ordinary video (the source viewer marks In and Out
   * on it). Present while the recording runs or is being ingested.
   */
  recordedTicks?: number;
  snapshotUrl?: string;
  /**
   * 1.9.1: the same recording as an open EVENT playlist (no #EXT-X-ENDLIST while it records), signed like the snapshot:
   * the source viewer plays it while it follows the live edge (Go live).
   */
  followUrl?: string;
}

/** Record what an encoder streams to a listener (RTMP or SRT), as received, plus a live proxy. */
export const LiveListenJobSchema = z.object({
  contractVersion: z.literal(CONTRACT_VERSION),
  correlationId: z.string(),
  recordingId: z.string().uuid(),
  protocol: z.enum(['rtmp', 'srt']),
  port: z.number().int().min(1024).max(65535),
  /** RTMP: the stream key (the path after /live/); SRT: the passphrase (10 to 79 characters). */
  secret: z.string().min(1).max(200),
  /** Give up when nothing connects for this long. */
  waitSeconds: z.number().int().min(10).max(7 * 24 * 3600),
  destination: z.object({ locationId: z.string().uuid(), prefix: z.string().min(1).max(500) }),
  master: z.object({ locationId: z.string().uuid(), key: z.string().min(1).max(1000) }),
  locations: LocationSpecsSchema,
});
export type LiveListenJob = z.infer<typeof LiveListenJobSchema>;

/** What the recorder reports while a stream is connected (shown in Monitor → Live). */
export interface LiveStreamStatus {
  /** "H.264 1920×1080 25 fps · AAC 48 kHz stereo"; null until FFmpeg has described the input. */
  format: string | null;
  /** The container as received: flv (RTMP) or mpegts (SRT). */
  container: string | null;
  /** Measured from the recording as it grows. */
  bitrateKbps: number | null;
  /**
   * 1.9.1: when the stream's first packet arrived (ms since the epoch: the first progress less its seconds), and the
   * frame rate FFmpeg reads from its header (fps), once known. Optional: recorders before 1.9.1 leave them out.
   */
  startedAtMs?: number | null;
  fps?: number | null;
}

export interface LiveListenResult {
  connected: boolean;
  /** 1.9.1: when the stream's first packet arrived (ms since the epoch), as in LiveStreamStatus. */
  startedAtMs?: number | null;
  seconds: number;
  sizeBytes: number;
  checksumSha256: string | null;
}

/** 1.9.1: where a live source's recordings take their start timecode from. */
export type LiveTimecodeMode = 'time_of_day' | 'stream';
export const LIVE_TIMECODE_MODES = ['time_of_day', 'stream'] as const;

export interface LiveSourceDTO {
  id: string;
  name: string;
  protocol: 'rtmp' | 'srt';
  port: number;
  site: string;
  armed: boolean;
  armedAt: string | null;
  /** What to type into the encoder or phone app (without the secret, which is shown only to people who manage sources). */
  url: string;
  /** RTMP stream key or SRT passphrase; only for storage managers. */
  secret: string | null;
  /** The recording in progress, if a stream is connected or awaited. */
  recording: { assetId: string | null; startedAt: string; state: 'waiting' | 'recording' } | null;
  /** 1.8.0 rights: the recording's rights values and dates relative to its start (rights.ts SourceRights); absent: none. */
  settings: {
    categoryId?: string | null; titlePrefix?: string; waitMinutes?: number; rights?: import('./rights.ts').SourceRights;
    /**
     * 1.9.1: the recordings' start timecode: 'time_of_day', the clock time the stream started in the workspace's time
     * zone (new sources); 'stream' (absent: sources made before 1.9.1), the file's own timecode or 00:00:00:00.
     */
    timecode?: LiveTimecodeMode;
  };
  createdAt: string;
}
